import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { router } from '@/routes';
import { queryClient } from '@/lib/query-client';
import { applyDirection, useUIStore } from '@/stores/ui-store';
import { onSignOut } from '@/lib/api';
import { syncFromStorage, useAuthStore } from '@/stores/auth-store';
import { STORAGE_KEYS } from '@/lib/constants';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ErrorBoundary } from '@/components/shared/error-boundary';
import { SkipLink } from '@/components/shared/skip-link';
import { CookieConsentBanner } from '@/components/shared/cookie-consent';
import { ConfirmDialogProvider } from '@/components/ui/confirm-dialog';
import { i18n } from '@/i18n';

export function App() {
  const locale = useUIStore((s) => s.locale);

  useEffect(() => {
    applyDirection(locale);
    if (i18n.language !== locale) void i18n.changeLanguage(locale);
  }, [locale]);

  // Cross-tab auth synchronization
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key === STORAGE_KEYS.authTokens || event.key === STORAGE_KEYS.adminAuthTokens) {
        syncFromStorage();
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Sign out on token refresh failure
  useEffect(() => {
    return onSignOut((flavor) => {
      const store = useAuthStore.getState();
      if (flavor === 'admin') store.signOutAdmin();
      else store.signOutUser();
    });
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ConfirmDialogProvider>
            <SkipLink />
            <RouterProvider router={router} />
            <CookieConsentBanner />
            <Toaster
              position="top-right"
              richColors
              closeButton
              theme="system"
              toastOptions={{
                classNames: {
                  toast: 'rounded-md border border-(--color-border) shadow-lg',
                },
              }}
            />
          </ConfirmDialogProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
